import { useMutation } from "@apollo/client";
import { UPDATE_FESTIVAL_SETTINGS } from "./graphql";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { Button, Container, TextField } from "@mui/material";
import "react-image-crop/dist/ReactCrop.css";
import { toast } from "react-toastify";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import semver from "semver";

import { PageContainer } from "../../components/PageContainer";
import { ButtonContainer } from "../../components/ButtonContainer";
import { Setting } from "../../__generated__/graphql";
import { Note } from "../../components/Note";
import { EditionPicker } from "../../components/EditionPIcker";
import dayjs from "dayjs";
import { ZonedDateTimePicker } from "../../components/ZonedDateTimePicker";
import { FaqLabel } from "../edition/edit/form";
import { triggerLiveEditionExport } from "../../client";

const menuItems = ["VIP", "Map", "Debug", "Showcase"];

const schema = yup
  .object({
    editionId: yup.string().required(),
    hiddenMenuItems: yup.string(),
    countdownDate: yup.date().nullable(),
    checkboxValues: yup
      .array()
      .of(yup.object({ name: yup.string(), checked: yup.bool() })),
  })
  .required();

export interface ISettingsProps {
  setting: Setting;
  versionNumber: string;
}

export const SettingsForm = ({ setting, versionNumber }: ISettingsProps) => {
  const defaultCheckboxes = menuItems.map((item) => {
    const checked = setting.hiddenMenuItems?.includes(item);

    return {
      name: item,
      checked: !!checked,
    };
  });

  const [updateSettings] = useMutation(UPDATE_FESTIVAL_SETTINGS, {
    onCompleted: () => {
      toast("settings updated");
      // IMPORTANT BUG FIX: auto-export only ever exports the live edition, so
      // if next year's event was prepared in full before switching to it, no
      // export file exists yet. The app (via lambda) would then have nothing
      // to read once the edition is switched. Export on switch to guarantee it.
      void triggerLiveEditionExport({ suppressToast: true });
    },
  });
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      editionId: setting.editionId,
      hiddenMenuItems: setting.hiddenMenuItems ?? "",
      checkboxValues: defaultCheckboxes,
      countdownDate: setting.countdownDate,
    },
  });
  const onSubmit = (val: any) => {
    const { editionId, checkboxValues, countdownDate } = val;

    const names = checkboxValues.flatMap((item: any) => {
      if (!item.checked) return [];
      return [item.name];
    });

    updateSettings({
      variables: {
        editionId,
        hiddenMenuItems: names.toString(),
        countdownDate,
      },
    });
  };
  const { fields, append, prepend, remove, swap, move, insert } = useFieldArray(
    {
      control, // control props comes from useForm (optional: if you are using FormContext)
      name: "checkboxValues", // unique name for your Field Array
    }
  );

  return (
    <PageContainer title="App Settings">
      <form onSubmit={handleSubmit(onSubmit)}>
        <Container style={{ flexDirection: "column", display: "flex" }}>
          <TextField
            disabled
            id="outlined-basic"
            label="Minimum version"
            variant="outlined"
            value={setting.minVersion}
          />
          <TextField
            disabled
            id="outlined-basic"
            label="Down for maintenance"
            variant="outlined"
            // defaultValue={false}
            value={setting.downForMaintenance}
          />
          <Controller
            control={control}
            name="editionId"
            render={({ field: { onChange, value } }) => (
              <>
                <EditionPicker
                  {...register("editionId")}
                  handleChange={onChange}
                  defaultValue={value}
                  value={value}
                />
              </>
            )}
          />
          <FaqLabel style={{ marginTop: 12 }}>
            This is the festival currently appearing in the app, we recommend
            publishing the lineup before you change this.
          </FaqLabel>
          <p>{errors.editionId?.message}</p>
          <Controller
            control={control}
            name="countdownDate"
            render={({ field: { onChange, value } }) => {
              console.log("date value", value);
              return (
                <ZonedDateTimePicker
                  format="YYYY/MM/DD HH:mm"
                  ampm={false}
                  label="Countdown Date"
                  defaultValue={value ? dayjs(value) : null}
                  // @ts-ignore
                  onChange={onChange}
                />
              );
            }}
          />
          <FaqLabel style={{ marginTop: -20, marginBottom: 20 }}>
            The date the app counts down to on the home screen{" "}
          </FaqLabel>

          <ButtonContainer>
            <Button type="submit" variant="contained">
              Save
            </Button>
          </ButtonContainer>
        </Container>
      </form>
    </PageContainer>
  );
};
